import React, { createContext, useContext } from 'react'
import { supabase } from '../lib/supabase'

const ApiContext = createContext({})

export const useApi = () => {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider')
  }
  return context
}

export const ApiProvider = ({ children }) => {
  // Customer APIs
  const getCustomers = async () => {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  }

  const getCustomerById = async (id) => {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('customer_id', id)
      .single()
    
    if (error) throw error
    return data
  }

  const createCustomer = async (customerData) => {
    const { data, error } = await supabase
      .from('customers')
      .insert([customerData])
      .select()
    
    if (error) throw error
    return data[0]
  }

  const updateCustomer = async (id, updates) => {
    const { data, error } = await supabase
      .from('customers')
      .update(updates)
      .eq('customer_id', id)
      .select()
    
    if (error) throw error
    return data[0]
  }

  // Claims APIs
  const getClaims = async (filters = {}) => {
    console.log('getClaims called with filters:', filters)
    
    // Start with a simple query to avoid foreign key issues
    let query = supabase
      .from('claims')
      .select('*')
      .order('created_at', { ascending: false })

    // Apply filters
    if (filters.customer_id) {
      console.log('Filtering by customer_id:', filters.customer_id)
      query = query.eq('customer_id', filters.customer_id)
    }
    if (filters.policy_id) {
      console.log('Filtering by policy_id:', filters.policy_id)
      query = query.eq('policy_id', filters.policy_id)
    }
    if (filters.claim_stage) {
      query = query.eq('claim_stage', filters.claim_stage)
    }
    if (filters.claim_type) {
      query = query.eq('claim_type', filters.claim_type)
    }
    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    const { data, error } = await query
    console.log('getClaims query result:', { data, error })
    if (error) throw error
    return data
  }

  const getClaimById = async (id) => {
    const { data, error } = await supabase
      .from('claims')
      .select(`
        *,
        customers!fk_claims_customer_id (
          first_name,
          last_name,
          email,
          customer_number,
          phone,
          city,
          state
        ),
        policies:policy_id (
          policy_number,
          policy_type_id,
          sum_insured,
          policy_start_date,
          policy_end_date
        ),
        hospitals:hospital_id (
          hospital_name,
          hospital_code,
          city,
          state,
          address
        ),
        diseases:disease_id (
          disease_name,
          icd_code
        )
      `)
      .eq('claim_id', id)
      .single()
    
    if (error) throw error
    return data
  }

  const createClaim = async (claimData) => {
    const { data, error } = await supabase
      .from('claims')
      .insert([claimData])
      .select()
    
    if (error) throw error
    return data[0]
  }

  const updateClaim = async (id, updates) => {
    const { data, error } = await supabase
      .from('claims')
      .update(updates)
      .eq('claim_id', id)
      .select()
    
    if (error) throw error
    return data[0]
  }

  // Policy Types APIs
  const getPolicyTypes = async () => {
    const { data, error } = await supabase
      .from('policy_types')
      .select('*')
      .eq('status', 'ACTIVE')
      .order('product_category', { ascending: true })
    
    if (error) throw error
    return data
  }

  const getPolicyTypesByCategory = async (category) => {
    const { data, error } = await supabase
      .from('policy_types')
      .select('*')
      .eq('status', 'ACTIVE')
      .eq('product_category', category)
      .order('base_sum_insured_min', { ascending: true })
    
    if (error) throw error
    return data
  }

  const getCoverageAmounts = async (policyTypeId) => {
    const { data, error } = await supabase
      .from('policy_types')
      .select('base_sum_insured_min, base_sum_insured_max')
      .eq('policy_type_id', policyTypeId)
      .single()
    
    if (error) throw error
    return data
  }

  // Policies APIs
  const getPolicies = async (filters = {}) => {
    let query = supabase
      .from('policies')
      .select(`
        *,
        customers!fk_policies_customer_id (
          first_name,
          last_name,
          email,
          customer_number
        ),
        policy_types:policy_type_id (
          product_name,
          product_description
        )
      `)
      .order('created_at', { ascending: false })

    if (filters.customer_id) {
      query = query.eq('customer_id', filters.customer_id)
    }
    if (filters.policy_type) {
      query = query.eq('policy_type', filters.policy_type)
    }
    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    const { data, error } = await query
    if (error) throw error
    return data
  }

  const getPolicyById = async (id) => {
    const { data, error } = await supabase
      .from('policies')
      .select(`
        *,
        customers!fk_policies_customer_id (
          first_name,
          last_name,
          email,
          customer_number,
          phone,
          city,
          state
        ),
        policy_types:policy_type_id (
          product_name,
          product_description,
          coverage_details
        )
      `)
      .eq('policy_id', id)
      .single()
    
    if (error) throw error
    return data
  }

  const createPolicy = async (policyData) => {
    const { data, error } = await supabase
      .from('policies')
      .insert([policyData])
      .select()
    
    if (error) throw error
    return data[0]
  }

  const updatePolicy = async (id, updates) => {
    const { data, error } = await supabase
      .from('policies')
      .update(updates)
      .eq('policy_id', id)
      .select()
    
    if (error) throw error
    return data[0]
  }

  // Hospitals APIs
  const getHospitals = async (filters = {}) => {
    let query = supabase
      .from('hospitals')
      .select('*')
      .order('hospital_name', { ascending: true })

    if (filters.city) {
      query = query.eq('city', filters.city)
    }
    if (filters.state) {
      query = query.eq('state', filters.state)
    }
    if (filters.hospital_type) {
      query = query.eq('hospital_type', filters.hospital_type)
    }

    const { data, error } = await query
    if (error) throw error
    return data
  }

  const getHospitalById = async (id) => {
    const { data, error } = await supabase
      .from('hospitals')
      .select('*')
      .eq('hospital_id', id)
      .single()
    
    if (error) throw error
    return data
  }

  // Diseases APIs
  const getDiseases = async (filters = {}) => {
    let query = supabase
      .from('diseases')
      .select('*')
      .order('disease_name', { ascending: true })

    if (filters.category) {
      query = query.eq('category', filters.category)
    }

    const { data, error } = await query
    if (error) throw error
    return data
  }

  // Dashboard Stats
  const getDashboardStats = async () => {
    try {
      const [
        customersResult, 
        claimsResult, 
        policiesResult, 
        hospitalsResult,
        diseasesResult,
        documentsResult,
        paymentsResult,
        policyTypesResult,
        coverageRulesResult,
        claimDecisionsResult,
        notificationsResult,
        apiLogsResult,
        sessionsResult,
        usersResult
      ] = await Promise.all([
        supabase.from('customers').select('customer_id', { count: 'exact' }),
        supabase.from('claims').select('claim_id', { count: 'exact' }),
        supabase.from('policies').select('policy_id', { count: 'exact' }),
        supabase.from('hospitals').select('hospital_id', { count: 'exact' }),
        supabase.from('diseases').select('disease_id', { count: 'exact' }),
        supabase.from('documents').select('document_id', { count: 'exact' }),
        supabase.from('payments').select('payment_id', { count: 'exact' }),
        supabase.from('policy_types').select('policy_type_id', { count: 'exact' }),
        supabase.from('coverage_rules').select('rule_id', { count: 'exact' }),
        supabase.from('claim_decisions').select('decision_id', { count: 'exact' }),
        supabase.from('notifications').select('notification_id', { count: 'exact' }),
        supabase.from('api_logs').select('id', { count: 'exact' }),
        supabase.from('sessions').select('session_id', { count: 'exact' }),
        supabase.from('users').select('user_id', { count: 'exact' })
      ])

      return {
        totalCustomers: customersResult.count || 0,
        totalClaims: claimsResult.count || 0,
        totalPolicies: policiesResult.count || 0,
        totalHospitals: hospitalsResult.count || 0,
        totalDiseases: diseasesResult.count || 0,
        totalDocuments: documentsResult.count || 0,
        totalPayments: paymentsResult.count || 0,
        totalPolicyTypes: policyTypesResult.count || 0,
        totalCoverageRules: coverageRulesResult.count || 0,
        totalClaimDecisions: claimDecisionsResult.count || 0,
        totalNotifications: notificationsResult.count || 0,
        totalApiLogs: apiLogsResult.count || 0,
        totalSessions: sessionsResult.count || 0,
        totalUsers: usersResult.count || 0
      }
    } catch (error) {
      console.error('Error in getDashboardStats:', error)
      throw error
    }
  }

  // Get filtered data with date ranges
  const getFilteredData = async (timeRange, startDate, endDate, claimStatus, paymentStatus) => {
    try {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()
      
      let fromDate, toDate
      
      // Calculate date range based on filter
      switch (timeRange) {
        case 'today':
        case 'mtd':
          fromDate = new Date(currentYear, currentMonth, 1)
          toDate = new Date(currentYear, currentMonth + 1, 0)
          break
        case 'ytd':
          fromDate = new Date(currentYear, 0, 1)
          toDate = new Date(currentYear, 11, 31)
          break
        case 'last_30_days':
          fromDate = new Date(currentDate.getTime() - 30 * 24 * 60 * 60 * 1000)
          toDate = currentDate
          break
        case 'last_90_days':
          fromDate = new Date(currentDate.getTime() - 90 * 24 * 60 * 60 * 1000)
          toDate = currentDate
          break
        case 'last_6_months':
          fromDate = new Date(currentYear, currentMonth - 6, 1)
          toDate = new Date(currentYear, currentMonth, 0)
          break
        case 'last_12_months':
          fromDate = new Date(currentYear - 1, currentMonth, 1)
          toDate = new Date(currentYear, currentMonth, 0)
          break
        case 'custom':
          if (startDate && endDate) {
            fromDate = new Date(startDate)
            toDate = new Date(endDate)
          } else {
            fromDate = new Date(currentYear, 0, 1)
            toDate = new Date(currentYear, 11, 31)
          }
          break
        default:
          fromDate = new Date(currentYear, 0, 1)
          toDate = new Date(currentYear, 11, 31)
      }
      
      // Format dates for Supabase
      const fromDateStr = fromDate.toISOString().split('T')[0]
      const toDateStr = toDate.toISOString().split('T')[0]
      
      console.log('Date range:', { fromDateStr, toDateStr, timeRange })
      
      // Build queries with date filters
      const claimsQuery = supabase
        .from('claims')
        .select('claim_id, created_at', { count: 'exact' })
        .gte('created_at', fromDateStr)
        .lte('created_at', toDateStr)
      
      const paymentsQuery = supabase
        .from('payments')
        .select('payment_id, created_at', { count: 'exact' })
        .gte('created_at', fromDateStr)
        .lte('created_at', toDateStr)
      
      const claimDecisionsQuery = supabase
        .from('claim_decisions')
        .select('decision_id, created_at', { count: 'exact' })
        .gte('created_at', fromDateStr)
        .lte('created_at', toDateStr)
      
      // Execute queries
      const [claimsResult, paymentsResult, claimDecisionsResult] = await Promise.all([
        claimsQuery,
        paymentsQuery,
        claimDecisionsQuery
      ])
      
      console.log('Filtered results:', {
        claims: claimsResult.count,
        payments: paymentsResult.count,
        claimDecisions: claimDecisionsResult.count
      })
      
      return {
        claims: claimsResult.count || 0,
        payments: paymentsResult.count || 0,
        claimDecisions: claimDecisionsResult.count || 0,
        claimsData: claimsResult.data || [],
        paymentsData: paymentsResult.data || [],
        claimDecisionsData: claimDecisionsResult.data || []
      }
    } catch (error) {
      console.error('Error in getFilteredData:', error)
      throw error
    }
  }

  // Test function to check sessions table specifically
  const testSessions = async () => {
    try {
      console.log('Testing sessions table...')
      
      // First, try to get the table structure
      const { data: tableData, error: tableError } = await supabase
        .from('sessions')
        .select('*')
        .limit(1)
      
      console.log('Sessions table test:', { tableData, tableError })
      
      // Show the actual column names from the first record
      if (tableData && tableData.length > 0) {
        const columnNames = Object.keys(tableData[0])
        console.log('Sessions column names:', columnNames)
        console.log('Sessions first record:', tableData[0])
      } else {
        console.log('No table data found for sessions')
      }
      
      // Try different possible primary key column names
      const possibleKeys = ['id', 'session_id', 'session_token', 'token']
      
      for (const key of possibleKeys) {
        try {
          const { data: testData, error: testError, count } = await supabase
            .from('sessions')
            .select(key, { count: 'exact' })
          
          if (!testError) {
            console.log(`Sessions count with ${key}:`, { count, testData })
            break
          } else {
            console.log(`Sessions count with ${key} failed:`, testError.message)
          }
        } catch (err) {
          console.log(`Sessions count with ${key} error:`, err.message)
        }
      }
      
      return { tableData, tableError }
    } catch (error) {
      console.error('Error testing sessions:', error)
      return { error }
    }
  }

  // Test function to check coverage rules table specifically
  const testCoverageRules = async () => {
    try {
      console.log('Testing coverage_rules table...')
      
      // First, try to get the table structure
      const { data: tableData, error: tableError } = await supabase
        .from('coverage_rules')
        .select('*')
        .limit(1)
      
      console.log('Coverage rules table test:', { tableData, tableError })
      
      // Show the actual column names from the first record
      if (tableData && tableData.length > 0) {
        const columnNames = Object.keys(tableData[0])
        console.log('Coverage rules column names:', columnNames)
        console.log('Coverage rules first record:', tableData[0])
      } else {
        console.log('No table data found for coverage rules')
      }
      
      // Try different possible primary key column names
      const possibleKeys = ['id', 'coverage_rule_id', 'rule_id', 'coverage_id']
      
      for (const key of possibleKeys) {
        try {
          const { data: testData, error: testError, count } = await supabase
            .from('coverage_rules')
            .select(key, { count: 'exact' })
          
          if (!testError) {
            console.log(`Coverage rules count with ${key}:`, { count, testData })
            break
          } else {
            console.log(`Coverage rules count with ${key} failed:`, testError.message)
          }
        } catch (err) {
          console.log(`Coverage rules count with ${key} error:`, err.message)
        }
      }
      
      return { tableData, tableError }
    } catch (error) {
      console.error('Error testing coverage rules:', error)
      return { error }
    }
  }

  // Generic API functions for all tables
  const getTableData = async (tableName, filters = {}) => {
    console.log(`🔍 Fetching data for table: ${tableName}`)
    console.log(`🔍 Filters applied:`, filters)
    
    let query = supabase.from(tableName).select('*')
    
    // Apply filters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        console.log(`🔍 Applying filter: ${key} = ${filters[key]}`)
        query = query.eq(key, filters[key])
      }
    })
    
    // Add ordering
    query = query.order('created_at', { ascending: false })
    
    const { data, error } = await query
    console.log(`📊 ${tableName} query result:`, { data: data?.length || 0, error })
    
    if (error) {
      console.error(`❌ Error fetching ${tableName}:`, error)
      throw error
    }
    
    console.log(`✅ Successfully fetched ${data?.length || 0} records from ${tableName}`)
    return data || []
  }

  const getTableDataById = async (tableName, id) => {
    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('id', id)
      .single()
    
    if (error) throw error
    return data
  }

  const createTableRecord = async (tableName, recordData) => {
    const { data, error } = await supabase
      .from(tableName)
      .insert([recordData])
      .select()
    
    if (error) throw error
    return data[0]
  }

  const updateTableRecord = async (tableName, id, updates, primaryKey = 'id') => {
    const { data, error } = await supabase
      .from(tableName)
      .update(updates)
      .eq(primaryKey, id)
      .select()
    
    if (error) throw error
    return data[0]
  }

  const deleteTableRecord = async (tableName, id, primaryKey = 'id') => {
    const { error } = await supabase
      .from(tableName)
      .delete()
      .eq(primaryKey, id)
    
    if (error) throw error
    return true
  }

  // Specific table APIs
  const getDocuments = async (filters = {}) => {
    return getTableData('documents', filters)
  }

  const getPayments = async (filters = {}) => {
    return getTableData('payments', filters)
  }

  const getCoverageRules = async (filters = {}) => {
    return getTableData('coverage_rules', filters)
  }

  const getClaimDecisions = async (filters = {}) => {
    return getTableData('claim_decisions', filters)
  }

  const getSessions = async (filters = {}) => {
    return getTableData('sessions', filters)
  }

  const getUsers = async (filters = {}) => {
    return getTableData('users', filters)
  }

  const getApiLogs = async (filters = {}) => {
    return getTableData('api_logs', filters)
  }

  const getNotifications = async (filters = {}) => {
    return getTableData('notifications', filters)
  }


  // User Policy Functions - Using clean function
  const getUserPolicies = async (userId) => {
    try {
      console.log('getUserPolicies called with userId:', userId)
      
      const { data, error } = await supabase.rpc('get_user_policies', {
        user_id_param: userId
      })
      
      if (error) {
        console.error('Error fetching user policies:', error)
        throw error
      }
      
      console.log('getUserPolicies result:', data)
      return data || []
    } catch (error) {
      console.error('Error in getUserPolicies:', error)
      throw error
    }
  }

  const getUserPoliciesWithClaims = async (userId) => {
    try {
      console.log('getUserPoliciesWithClaims called with userId:', userId)
      
      // Use the corrected SQL function that fixes data type and column issues
      const { data, error } = await supabase.rpc('get_user_policies_with_claims', {
        user_id_param: userId
      })
      
      if (error) {
        console.error('Error fetching user policies with claims:', error)
        throw error
      }
      
      console.log('getUserPoliciesWithClaims result:', data)
      return data || []
    } catch (error) {
      console.error('Error in getUserPoliciesWithClaims:', error)
      throw error
    }
  }

  const value = {
    // Customer APIs
    getCustomers,
    getCustomerById,
    createCustomer,
    updateCustomer,
    
    // Claims APIs
    getClaims,
    getClaimById,
    createClaim,
    updateClaim,
    
    // Policy Types APIs
    getPolicyTypes,
    getPolicyTypesByCategory,
    getCoverageAmounts,
    
    // Policies APIs
    getPolicies,
    getPolicyById,
    createPolicy,
    updatePolicy,
    
    // Hospitals APIs
    getHospitals,
    getHospitalById,
    
    
    // Dashboard
    getDashboardStats,
    getFilteredData,
    testCoverageRules,
    testSessions,

    // Generic table APIs
    getTableData,
    getTableDataById,
    createTableRecord,
    updateTableRecord,
    deleteTableRecord,

    // Specific table APIs
    getDocuments,
    getPayments,
    getCoverageRules,
    getClaimDecisions,
    getSessions,
    getUsers,
    getApiLogs,
    getNotifications,
    
    // User Policy APIs
    getUserPolicies,
    getUserPoliciesWithClaims
  }

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  )
}